import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../apiClient";
import { formatNumber } from "../formatNumber";

const getNetAmount = (item) => Number(item?.TotalCredits || 0) - Number(item?.TotalDebits || 0);

const ReportsPeriodTransactions = () => {
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState("");
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ TotalCredits: 0, TotalDebits: 0, TotalSavings: 0 });
  const [expandedCategoryId, setExpandedCategoryId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const sortedPeriods = useMemo(
    () => [...periods].sort((a, b) => (a.Description || "").localeCompare(b.Description || "")),
    [periods]
  );
  const maxExpenditure = useMemo(
    () =>
      rows.reduce(
        (max, row) => Math.max(max, Math.abs(getNetAmount(row))),
        0
      ),
    [rows]
  );
  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const amountA = Math.abs(getNetAmount(a));
        const amountB = Math.abs(getNetAmount(b));

        if (amountB !== amountA) return amountB - amountA;
        return (a.Category || "").localeCompare(b.Category || "");
      }),
    [rows]
  );

  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        const res = await apiClient.get("/periods");
        const periodRows = Array.isArray(res.data) ? res.data : [];
        setPeriods(periodRows);
        if (periodRows.length > 0) setPeriodId(String(periodRows[0].PeriodId));
      } catch (err) {
        setError("Failed to load periods.");
      } finally {
        setLoading(false);
      }
    };

    fetchPeriods();
  }, []);

  useEffect(() => {
    const fetchReport = async () => {
      if (!periodId) {
        setRows([]);
        return;
      }

      setError("");
      setLoading(true);
      setExpandedCategoryId("");
      try {
        const res = await apiClient.get("/reports/period-subcategory", { params: { periodId } });
        setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
        setTotals({
          TotalCredits: Number(res.data?.totals?.TotalCredits || 0),
          TotalDebits: Number(res.data?.totals?.TotalDebits || 0),
          TotalSavings: Number(res.data?.totals?.TotalSavings || 0),
        });
      } catch (err) {
        setError("Failed to load category summary.");
        setRows([]);
        setTotals({ TotalCredits: 0, TotalDebits: 0, TotalSavings: 0 });
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [periodId]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Period Transaction Summary</h1>
          <p className="page-subtitle">
            <Link to="/">Home</Link> / <Link to="/Reports">Reports</Link> / Period Transaction Summary
          </p>
        </div>
      </header>

      <section className="panel form-card">
        <div className="form">
          <label htmlFor="period-select">Reporting Period</label>
          <select id="period-select" value={periodId} onChange={(e) => setPeriodId(e.target.value)}>
            {sortedPeriods.map((period) => (
              <option key={period.PeriodId} value={period.PeriodId}>
                {period.Description}
              </option>
            ))}
          </select>
        </div>
      </section>

      {error && <p className="notice notice-error page-cta">{error}</p>}
      {loading && <p className="notice notice-muted page-cta">Loading report...</p>}

      {!loading && (
        <section className="panel page-cta">
          <h2>Income vs Savings</h2>
          <div className="bar-chart">
            <div className="bar-chart-row">
              <div className="bar-chart-label">Income</div>
              <div className="bar-chart-track">
                <div
                  className="bar-chart-fill bar-chart-fill-credit"
                  style={{
                    width: `${((totals.TotalCredits || 0) / Math.max(totals.TotalCredits || 0, totals.TotalSavings || 0, 1)) * 100}%`,
                  }}
                />
              </div>
              <div className="bar-chart-value">{formatNumber(totals.TotalCredits)}</div>
            </div>
            <div className="bar-chart-row">
              <div className="bar-chart-label">Savings</div>
              <div className="bar-chart-track">
                <div
                  className="bar-chart-fill bar-chart-fill-credit"
                  style={{
                    width: `${((totals.TotalSavings || 0) / Math.max(totals.TotalCredits || 0, totals.TotalSavings || 0, 1)) * 100}%`,
                  }}
                />
              </div>
              <div className="bar-chart-value">{formatNumber(totals.TotalSavings)}</div>
            </div>
          </div>
        </section>
      )}

      {!loading && (
        <section className="panel page-cta">
          <h2>Credits vs Debits</h2>
          <div className="bar-chart">
            <div className="bar-chart-row">
              <div className="bar-chart-label">Credits</div>
              <div className="bar-chart-track">
                <div
                  className="bar-chart-fill bar-chart-fill-credit"
                  style={{
                    width: `${((totals.TotalCredits || 0) / Math.max(totals.TotalCredits || 0, totals.TotalDebits || 0, 1)) * 100}%`,
                  }}
                />
              </div>
              <div className="bar-chart-value">{formatNumber(totals.TotalCredits)}</div>
            </div>
            <div className="bar-chart-row">
              <div className="bar-chart-label">Debits</div>
              <div className="bar-chart-track">
                <div
                  className="bar-chart-fill bar-chart-fill-debit"
                  style={{
                    width: `${((totals.TotalDebits || 0) / Math.max(totals.TotalCredits || 0, totals.TotalDebits || 0, 1)) * 100}%`,
                  }}
                />
              </div>
              <div className="bar-chart-value">{formatNumber(totals.TotalDebits)}</div>
            </div>
          </div>
          {Number(totals.TotalCredits || 0) === 0 && (
            <p className="notice notice-muted page-cta">No credit transactions were recorded for the selected period.</p>
          )}
        </section>
      )}

      {!loading && (
        <section className="panel page-cta">
          <h2>Category Summary</h2>
          {rows.length === 0 ? (
            <p className="notice notice-muted">No category activity for this period.</p>
          ) : (
            <div className="bar-chart">
              {sortedRows.map((row) => {
                const netAmount = getNetAmount(row);
                const isCredit = netAmount >= 0;

                return (
                <div key={row.CategoryId || row.Category} className="period-category-block">
                  <button
                    type="button"
                    className="bar-chart-row period-category-toggle"
                    onClick={() =>
                      setExpandedCategoryId((current) =>
                        current === String(row.CategoryId || row.Category)
                          ? ""
                          : String(row.CategoryId || row.Category)
                      )
                    }
                  >
                    <div className="bar-chart-label">{row.Category || "-"}</div>
                    <div className="bar-chart-track">
                      <div
                        className={`bar-chart-fill ${isCredit ? "bar-chart-fill-credit" : "bar-chart-fill-debit"}`}
                        style={{
                          width: `${(
                            (Math.abs(netAmount) / Math.max(maxExpenditure, 1)) *
                            100
                          ).toFixed(2)}%`,
                        }}
                      />
                    </div>
                    <div className="bar-chart-value">
                      {formatNumber(netAmount)}
                    </div>
                  </button>

                  {expandedCategoryId === String(row.CategoryId || row.Category) && (
                    <div className="period-subcategory-panel">
                      {(row.Subcategories || []).length === 0 ? (
                        <p className="notice notice-muted">No subcategory breakdown available.</p>
                      ) : (
                        <div className="period-subcategory-list">
                          {row.Subcategories.map((subcategory) => (
                            <div key={subcategory.SubcategoryId || subcategory.Subcategory} className="period-subcategory-row">
                              <span>{subcategory.Subcategory || "-"}</span>
                              <strong>{formatNumber(getNetAmount(subcategory))}</strong>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default ReportsPeriodTransactions;
